import {
  isIdempotencyCapable,
  readSettled,
  recordSettled,
  type SettledStatus,
} from './billingReturnLedger'

export type BillingReturnParam = 'stripe_session' | 'package_downgraded'

export interface BillingReturnNotice {
  tone: 'success' | 'error'
  key:
    | 'credits.billing.return.success'
    | 'credits.billing.return.downgraded'
    | 'credits.billing.return.failed'
}

export interface BillingReturnDependencies {
  // Module 07 owns the HTTP + hydration. Module 11 only triggers the public reload seam (ledger B6).
  reloadAccount: () => Promise<{ status: 'skipped' | 'reloaded' | 'failed' }>
  refreshCredits: () => Promise<void>
  notify: (notice: BillingReturnNotice) => void
}

export interface BillingReturnOutcome {
  status: 'confirmed' | 'failed'
  destination: { name: 'Summary' }
}

const SUMMARY = { name: 'Summary' } as const

// Query callbacks and the dedicated view are mutually exclusive at routing level, but a component
// remount or a second caller can still overlap the same provider return. Share only the ACTIVE run;
// delete it after settle so a later real purchase/downgrade can refresh again.
const activeReturns = new Map<string, Promise<BillingReturnOutcome>>()

export class BillingReturnFlow {
  constructor(private readonly dependencies: BillingReturnDependencies) {}

  execute(param: BillingReturnParam, idempotencyKey = ''): Promise<BillingReturnOutcome> {
    const key = `${param}:${idempotencyKey}`
    const active = activeReturns.get(key)
    if (active) return active

    // A return that already settled in this tab must not reload the account context a second time
    // (contract 11 §269). Replaying it also re-toasts a completed payment, which reads as a second
    // charge. Redirect on the recorded outcome instead — no reload, no refresh, no notice.
    const replayable = isIdempotencyCapable(param, idempotencyKey)
    if (replayable) {
      const settled = readSettled(key)
      if (settled) return Promise.resolve({ status: settled, destination: SUMMARY })
    }

    const pending = this.run(param).then((outcome) => {
      if (replayable) recordSettled(key, outcome.status satisfies SettledStatus)
      return outcome
    })
    activeReturns.set(key, pending)
    const release = (): void => {
      if (activeReturns.get(key) === pending) activeReturns.delete(key)
    }
    void pending.then(release, release)
    return pending
  }

  private async run(param: BillingReturnParam): Promise<BillingReturnOutcome> {
    let confirmed = false
    try {
      const reload = await this.dependencies.reloadAccount()
      if (reload.status !== 'reloaded') throw new Error('Account context was not reloaded')
      await this.dependencies.refreshCredits()
      confirmed = true
    } catch {
      // A failed reload must never trap the user on the provider-return route. The flow owns the
      // soft outcome only; the classified account error remains module 07's responsibility.
    }

    if (confirmed) {
      this.safeNotify({
        tone: 'success',
        key:
          param === 'package_downgraded'
            ? 'credits.billing.return.downgraded'
            : 'credits.billing.return.success',
      })
      return { status: 'confirmed', destination: SUMMARY }
    }

    this.safeNotify({ tone: 'error', key: 'credits.billing.return.failed' })
    return { status: 'failed', destination: SUMMARY }
  }

  private safeNotify(notice: BillingReturnNotice): void {
    try {
      this.dependencies.notify(notice)
    } catch {
      // Toast infrastructure is secondary to the terminal redirect.
    }
  }
}
